import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Shield, Save, Loader2, Pencil, X } from "lucide-react";
import api from "../../../services/api";

var STATUS_OPTIONS = [
  { value: "ativa", label: "Ativa" },
  { value: "pendente", label: "Pendente" },
  { value: "vencida", label: "Vencida" },
  { value: "cancelada", label: "Cancelada" },
];

var TYPE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "residencial", label: "Residencial" },
  { value: "vida", label: "Vida" },
  { value: "empresarial", label: "Empresarial" },
];

function formatCurrency(v) {
  if (v == null || v === 0) return "R$ 0,00";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return d; }
}

export default function PolicyEditModal({ policy, open, onClose, onSaved }) {
  var editingState = useState(false);
  var editing = editingState[0];
  var setEditing = editingState[1];

  var formState = useState({
    policy_number: policy?.policy_number || "",
    insurance_type: policy?.insurance_type || "auto",
    premium: policy?.premium || 0,
    deductible: policy?.deductible || 0,
    start_date: policy?.start_date || "",
    end_date: policy?.end_date || "",
    status: policy?.status || "ativa",
    notes: policy?.notes || "",
    coverage_details: policy?.coverage_details || "",
  });
  var form = formState[0];
  var setForm = formState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  function handleChange(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  function handleNumberChange(field, value) {
    var parsed = value === "" ? 0 : parseFloat(value);
    if (isNaN(parsed)) parsed = 0;
    handleChange(field, parsed);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      await api.put("/policies/" + policy.id, form);
      setEditing(false);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  function handleClose() {
    setEditing(false);
    setError(null);
    onClose();
  }

  if (!policy) return null;

  var statusInfo = STATUS_OPTIONS.find(function(s) { return s.value === (editing ? form.status : policy.status); }) || { label: policy.status };
  var typeInfo = TYPE_OPTIONS.find(function(t) { return t.value === (editing ? form.insurance_type : policy.insurance_type); }) || { label: policy.insurance_type };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2F3D]">
            <Shield className="w-5 h-5 text-[#3B82F6]" />
            {editing ? "Editar Apolice" : "Detalhes da Apolice"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2" data-testid="policy-edit-modal">
          {/* Row: Numero + Tipo */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Numero da Apolice</label>
              {editing ? (
                <input type="text" value={form.policy_number} onChange={function(e) { handleChange("policy_number", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  data-testid="policy-edit-number" />
              ) : (
                <p className="text-sm font-medium text-[#0A2F3D]" data-testid="policy-detail-number">{policy.policy_number || "-"}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Tipo de Seguro</label>
              {editing ? (
                <select value={form.insurance_type} onChange={function(e) { handleChange("insurance_type", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 bg-white"
                  data-testid="policy-edit-type">
                  {TYPE_OPTIONS.map(function(t) {
                    return <option key={t.value} value={t.value}>{t.label}</option>;
                  })}
                </select>
              ) : (
                <p className="text-sm text-[#0A2F3D]">{typeInfo.label}</p>
              )}
            </div>
          </div>

          {/* Seguradora (read-only) */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Seguradora</label>
            <p className="text-sm text-[#0A2F3D]">{policy.insurer_name || "-"}</p>
          </div>

          {/* Row: Premio + Franquia */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Premio (R$)</label>
              {editing ? (
                <input type="number" step="0.01" value={form.premium || ""} onChange={function(e) { handleNumberChange("premium", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  data-testid="policy-edit-premium" />
              ) : (
                <p className="text-sm font-semibold text-[#0A2F3D]">{formatCurrency(policy.premium)}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Franquia (R$)</label>
              {editing ? (
                <input type="number" step="0.01" value={form.deductible || ""} onChange={function(e) { handleNumberChange("deductible", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  data-testid="policy-edit-deductible" />
              ) : (
                <p className="text-sm font-semibold text-[#0A2F3D]">{formatCurrency(policy.deductible)}</p>
              )}
            </div>
          </div>

          {/* Row: Vigencia */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Inicio Vigencia</label>
              {editing ? (
                <input type="date" value={form.start_date} onChange={function(e) { handleChange("start_date", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  data-testid="policy-edit-start" />
              ) : (
                <p className="text-sm text-[#0A2F3D]">{formatDate(policy.start_date)}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Fim Vigencia</label>
              {editing ? (
                <input type="date" value={form.end_date} onChange={function(e) { handleChange("end_date", e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                  data-testid="policy-edit-end" />
              ) : (
                <p className="text-sm text-[#0A2F3D]">{formatDate(policy.end_date)}</p>
              )}
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
            {editing ? (
              <select value={form.status} onChange={function(e) { handleChange("status", e.target.value); }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 bg-white"
                data-testid="policy-edit-status">
                {STATUS_OPTIONS.map(function(s) {
                  return <option key={s.value} value={s.value}>{s.label}</option>;
                })}
              </select>
            ) : (
              <span className={"inline-block px-2.5 py-1 text-xs font-medium rounded-full " + (
                policy.status === "ativa" ? "bg-emerald-100 text-emerald-700" :
                policy.status === "pendente" ? "bg-amber-100 text-amber-700" :
                policy.status === "vencida" ? "bg-red-100 text-red-700" :
                "bg-gray-200 text-gray-600"
              )}>{statusInfo.label}</span>
            )}
          </div>

          {/* Detalhes de Cobertura */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Detalhes da Cobertura</label>
            {editing ? (
              <textarea value={form.coverage_details} onChange={function(e) { handleChange("coverage_details", e.target.value); }}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 resize-none"
                data-testid="policy-edit-coverage" />
            ) : (
              <p className="text-sm text-gray-600">{policy.coverage_details || "Sem detalhes"}</p>
            )}
          </div>

          {/* Notas */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Observacoes</label>
            {editing ? (
              <textarea value={form.notes} onChange={function(e) { handleChange("notes", e.target.value); }}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 resize-none"
                data-testid="policy-edit-notes" />
            ) : (
              <p className="text-sm text-gray-600">{policy.notes || "Sem observacoes"}</p>
            )}
          </div>

          {/* Error */}
          {error && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            {editing ? (
              <>
                <button onClick={function() { setEditing(false); setError(null); }}
                  className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-1.5"
                  data-testid="policy-edit-cancel">
                  <X className="w-3.5 h-3.5" />Cancelar
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="px-4 py-2 text-xs font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="policy-edit-save">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Salvar
                </button>
              </>
            ) : (
              <button onClick={function() { setEditing(true); }}
                className="px-4 py-2 text-xs font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg transition-colors flex items-center gap-1.5"
                data-testid="policy-edit-btn">
                <Pencil className="w-3.5 h-3.5" />Editar
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
